import React, { useState, useEffect } from 'react';
import { FaLinkedin, FaInstagram, FaGraduationCap, FaBook, FaMapMarkerAlt } from 'react-icons/fa';
import api from "../../services/api";
import Loading from "../../components/loading";
import defaultAvatar from "../../assets/images/png/default-avatar.jpg";
import './style.css';

export default function InnerPage() {
    const [activeSection, setActiveSection] = useState(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [members, setMembers] = useState({
        exAlunos: [],
        homenageados: [],
        moradores: [],
        bixos: []
    });
    const [loading, setLoading] = useState(true);

    const hasBixos = Array.isArray(members.bixos) && members.bixos.length > 0;
    const sectionItems = [
        { id: 'ex-alunos', label: 'Ex-Alunos' },
        { id: 'homenageados', label: 'Homenageados' },
        { id: 'moradores', label: 'Moradores' },
        ...(hasBixos ? [{ id: 'bixos', label: 'Bixos' }] : [])
    ];
    const sections = sectionItems.map(section => section.id);

    const progressHeight = `${scrollProgress * 100}%`;
    const progressWidth = `${scrollProgress * 100}%`;

    useEffect(() => {
        setLoading(true);

        // Promise para delay mínimo de loading
        const minLoadingTime = new Promise(resolve => setTimeout(resolve, 800));

        // Promise para buscar dados
        const fetchData = api.get("/api/members-public");

        // Aguarda ambas as promises (dados carregados E tempo mínimo passado)
        Promise.all([fetchData, minLoadingTime])
            .then(([response]) => {
                const data = response.data || {};
                setMembers({
                    exAlunos: Array.isArray(data.exAlunos) ? data.exAlunos : [],
                    homenageados: Array.isArray(data.homenageados) ? data.homenageados : [],
                    moradores: Array.isArray(data.moradores) ? data.moradores : [],
                    bixos: Array.isArray(data.bixos) ? data.bixos : []
                });
                setLoading(false);
            })
            .catch((err) => {
                console.error("Erro ao buscar membros:", err);
                // Mesmo com erro, aguarda o tempo mínimo antes de esconder o loading
                setTimeout(() => setLoading(false), 800);
            });
    }, []);

    useEffect(() => {
        if (!hasBixos && activeSection === 'bixos') {
            setActiveSection(null);
        }
    }, [hasBixos, activeSection]);

    // Detectar seção ativa durante scroll
    useEffect(() => {
        let frameId = null;

        const updateScrollState = () => {
            const scrollPosition = window.scrollY + 200;
            const anchorPosition = window.scrollY + window.innerHeight * 0.4;

            const sectionElements = sections
                .map((sectionId) => document.getElementById(sectionId))
                .filter(Boolean);

            if (sectionElements.length > 1) {
                let nextProgress = 0;

                if (anchorPosition <= sectionElements[0].offsetTop) {
                    nextProgress = 0;
                } else {
                    for (let index = 0; index < sectionElements.length; index += 1) {
                        const currentSection = sectionElements[index];
                        const nextSection = sectionElements[index + 1];

                        if (!nextSection) {
                            nextProgress = 1;
                            break;
                        }

                        if (anchorPosition >= currentSection.offsetTop && anchorPosition < nextSection.offsetTop) {
                            const segmentProgress = (anchorPosition - currentSection.offsetTop) /
                                Math.max(nextSection.offsetTop - currentSection.offsetTop, 1);
                            nextProgress = (index + segmentProgress) / (sectionElements.length - 1);
                            break;
                        }
                    }
                }

                setScrollProgress(Math.max(0, Math.min(1, nextProgress)));
            }

            let matchedSection = null;

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        matchedSection = section;
                        break;
                    }
                }
            }

            setActiveSection(matchedSection);
        };

        const handleScroll = () => {
            if (frameId !== null) {
                return;
            }

            frameId = window.requestAnimationFrame(() => {
                updateScrollState();
                frameId = null;
            });
        };

        updateScrollState();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, [sections]);

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const MemberCard = ({ member, photoField = "photoGraduationFile" }) => {
        // Formata a data de graduação
        const formatGraduation = (dateString) => {
            if (!dateString) return null;
            const date = new Date(dateString);
            return date.getFullYear();
        };

        const graduationYear = formatGraduation(member.graduation);

        return (
            <div className="member-card">
                <div className="member-photo">
                    <img 
                        src={member[photoField] || defaultAvatar} 
                        alt={member.name} 
                        onError={(e) => { e.target.src = defaultAvatar }}
                    />
                    <div className="photo-overlay"></div>
                </div>
                <div className="member-info">
                    <div className="member-header">
                        <h3>{member.name}</h3>
                        {member.nickname && (
                            <p className="member-nickname">"{member.nickname}"</p>
                        )}
                    </div>

                    <div className="member-details">
                        {graduationYear && (
                            <div className="detail-item">
                                <FaGraduationCap className="detail-icon" />
                                <span>
                                    <strong>Formatura:</strong> {graduationYear}
                                </span>
                            </div>
                        )}
                        {member.course && (
                            <div className="detail-item">
                                <FaBook className="detail-icon" />
                                <span>
                                    <strong>Curso:</strong> {member.course}
                                </span>
                            </div>
                        )}
                        {member.hometown && (
                            <div className="detail-item">
                                <FaMapMarkerAlt className="detail-icon" />
                                <span>
                                    <strong>Cidade Natal:</strong> {member.hometown}
                                </span>
                            </div>
                        )}
                    </div>

                    {(member.linkedin || member.instagram) && (
                        <div className="member-socials">
                            {member.linkedin && (
                                <a
                                    href={member.linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="social-link linkedin"
                                    title="LinkedIn"
                                >
                                    <FaLinkedin />
                                </a>
                            )}
                            {member.instagram && (
                                <a
                                    href={member.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="social-link instagram"
                                    title="Instagram"
                                >
                                    <FaInstagram />
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="members-public-container">
                <section className="hero-section">
                    <div className="hero-content">
                        <h1>Perdidos</h1>
                    </div>
                </section>
                <div className="loading-container">
                    <Loading />
                </div>
            </div>
        );
    }

    return (
        <div className="members-public-container">
            {/* Navegador Lateral Fixo */}
            <nav className="side-navigation">
                <div className="nav-items">
                    <div className="nav-track">
                        <div className="nav-progress" style={{ height: progressHeight }} />
                    </div>
                    {sectionItems.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => scrollToSection(section.id)}
                            className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                            title={section.label}
                        >
                            <span className="nav-dot"></span>
                            <span className="nav-label">{section.label}</span>
                        </button>
                    ))}
                </div>
            </nav>

            {/* Navegação Mobile Fixa */}
            <nav className="mobile-section-nav" aria-label="Navegação por seções">
                <div className="mobile-nav-progress-track">
                    <div className="mobile-nav-progress" style={{ width: progressWidth }} />
                </div>
                <div className="mobile-nav-scroll">
                    {sectionItems.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => scrollToSection(section.id)}
                            className={`mobile-nav-btn ${activeSection === section.id ? 'active' : ''}`}
                            aria-current={activeSection === section.id ? 'true' : 'false'}
                        >
                            {section.label}
                        </button>
                    ))}
                </div>
            </nav>

            {/* Hero Section com Navegação */}
            <section className="hero-section">
                <div className="hero-content">
                    <h1>Perdidos</h1>
                    <div className="hero-divider"></div>
                    <div className="navigation-shortcuts">
                        {sectionItems.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => scrollToSection(section.id)}
                                className={`shortcut-btn ${activeSection === section.id ? 'active' : ''}`}
                                aria-current={activeSection === section.id ? 'true' : 'false'}
                            >
                                {section.label}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Seção Ex-Alunos */}
            <section id="ex-alunos" className="members-section">
                <div className="section-header">
                    <h2>Ex-Alunos</h2>
                    <div className="header-line"></div>
                </div>
                <div className="members-grid">
                    {[...members.exAlunos]
                        .sort((a, b) => {
                            if (!a.graduation) return 1;
                            if (!b.graduation) return -1;
                            return new Date(a.graduation) - new Date(b.graduation);
                        })
                        .map(member => (
                        <MemberCard key={member._id} member={member} />
                    ))}
                </div>
            </section>

            {/* Seção Homenageados */}
            <section id="homenageados" className="members-section">
                <div className="section-header">
                    <h2>Homenageados</h2>
                    <div className="header-line"></div>
                </div>
                <div className="members-grid">
                    {[...members.homenageados]
                        .sort((a, b) => {
                            if (!a.graduation) return 1;
                            if (!b.graduation) return -1;
                            return new Date(a.graduation) - new Date(b.graduation);
                        })
                        .map(member => (
                        <MemberCard key={member._id} member={member} />
                    ))}
                </div>
            </section>

            {/* Seção Moradores */}
            <section id="moradores" className="members-section">
                <div className="section-header">
                    <h2>Moradores</h2>
                    <div className="header-line"></div>
                </div>
                <div className="members-grid">
                    {members.moradores.map(member => (
                        <MemberCard key={member._id} member={member} photoField="photoNewFile" />
                    ))}
                </div>
            </section>

            {hasBixos && (
                <section id="bixos" className="members-section">
                    <div className="section-header">
                        <h2>Bixos</h2>
                        <div className="header-line"></div>
                    </div>
                    <div className="members-grid">
                        {members.bixos.map(member => (
                            <MemberCard
                                key={member._id}
                                member={member}
                                photoField={member.photoNewFile ? "photoNewFile" : "photoGraduationFile"}
                            />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
